/*
  Desktop-only Swiper for the collection list.

  The same <ul> is the theme's native slider-component track on mobile (CSS scroll-snap via the
  .slider / grid--peek classes) and a Swiper on desktop. Those two cannot coexist: Swiper drives
  a transform on the wrapper while the native slider relies on overflow scrolling.

  So the swiper-* classes are NOT rendered in Liquid -- they are added here at >= 990px and
  removed again below it, and Swiper is destroyed with cleanStyles on the way down. That keeps
  the mobile slider and the plain desktop grid byte-for-byte as they were when the toggle is off
  or the viewport is narrow.
*/
if (!customElements.get('collection-list-slider')) {
  class CollectionListSlider extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.collection-list');
      this.sliderComponent = this.querySelector('slider-component');
      if (!this.list || !this.sliderComponent) return;

      this.slides = [...this.list.children];
      // Tablet and up. Below 750px the theme's native slider-component keeps running.
      this.desktop = window.matchMedia('(min-width: 750px)');

      this.onBreakpointChange = () => this.sync();
      this.desktop.addEventListener('change', this.onBreakpointChange);
      this.sync();
    }

    disconnectedCallback() {
      this.desktop?.removeEventListener('change', this.onBreakpointChange);
      this.teardown();
    }

    sync() {
      if (this.desktop.matches) {
        this.build();
      } else {
        this.teardown();
      }
    }

    build() {
      if (this.swiper || typeof Swiper === 'undefined') return;

      this.sliderComponent.classList.add('swiper');
      this.list.classList.add('swiper-wrapper');
      this.slides.forEach((slide) => slide.classList.add('swiper-slide'));

      const prevButton = this.querySelector('.collection-list-slider__button--prev');
      const nextButton = this.querySelector('.collection-list-slider__button--next');
      const updateButtons = (swiper) => {
        if (prevButton) prevButton.disabled = swiper.isBeginning;
        if (nextButton) nextButton.disabled = swiper.isEnd;
      };

      this.swiper = new Swiper(this.sliderComponent, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        watchOverflow: true,
        a11y: true,
        on: {
          init: updateButtons,
          slideChange: updateButtons,
          resize: updateButtons,
        },
      });

      prevButton?.addEventListener('click', this.onPrev);
      nextButton?.addEventListener('click', this.onNext);
      this.prevButton = prevButton;
      this.nextButton = nextButton;
    }

    onPrev = () => this.swiper?.slidePrev();

    onNext = () => this.swiper?.slideNext();

    teardown() {
      if (!this.swiper) return;

      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);

      // cleanStyles: true so Swiper's inline transform/width are removed and the native
      // mobile slider gets its untouched markup back.
      this.swiper.destroy(true, true);
      this.swiper = null;

      this.sliderComponent.classList.remove('swiper');
      this.list.classList.remove('swiper-wrapper');
      this.slides.forEach((slide) => slide.classList.remove('swiper-slide'));
    }
  }

  customElements.define('collection-list-slider', CollectionListSlider);
}
